import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'components/Form/DatePicker';
import { DATE, LOCATIONS } from 'constants';
import { isSameDayBeforePickupHours } from 'utils/date';
import { i18n } from 'utils/i18n';
import { getCurrentDateTimeObj, getDateTimeObjFromTs } from '@ehi/global-marketing-interface/lib/utils/app/dateTime';
import { isLocationAvailableByTime } from '@ehi/global-marketing-interface/lib/utils/app/reservationDateTime';
import { isObjectEmpty } from '@ehi/global-marketing-interface/lib/utils/js/object';
import ResponsiveListbox from 'components/Form/ResponsiveListbox';

const GMI_TIME_FORMAT = 'HH:mm';

// Suggest "9:00 am"
const SUGGESTED_TIME = '09:00'; // option values are always in this format, regardless of regional format

const DateTimeFieldset = (props) => {
  const {
    locationId,
    startDate,
    endDate,
    dateName,
    getBranchHours,
    hoursForStore,
    onDateChange,
    onTimeChange,
    timeName,
    selectsStart,
    eligibility,
    locationTimezone,
    pickupTime,
    one_way_rental,
    dateId,
    dateLabel,
    selectsEnd,
    helpText,
    guided,
    showErrorsOnLoad,
    isSixColumnGrid,
    monthsShownDatePicker,
    timeAndDatePickerFullHeight,
    contractRestrictions,
    isUnavailableLocationContent,
    time,
    timeLabel,
    latestDate,
    pickupLocationType,
    accessibilityPrefixText,
  } = props;

  const today = getCurrentDateTimeObj();
  const [minDate] = useState(today.toDate());
  const [maxDate] = useState(today.add(1, 'years').toDate());
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [locationIdState, setLocationId] = useState(null);
  const [startDateState, setStartDate] = useState(null);
  const [endDateState, setEndDate] = useState(null);

  const isLocationTypeBranch = pickupLocationType === LOCATIONS.TYPE_BRANCH;

  useEffect(() => {
    if (dateName === 'pickupDate' && startDate !== startDateState) {
      setStartDate(startDate);
      setLastUpdated(Date.now());
    } else if (dateName === 'returnDate' && endDate !== endDateState) {
      setEndDate(endDate);
      setLastUpdated(Date.now());
    }

    if (locationId !== locationIdState) {
      setLocationId(locationId);
      setLastUpdated(Date.now());
    }
  }, [startDate, endDate, locationId, dateName]);

  const handleGetBranchHours = () => {
    //  If we dont have hours for store ID - get them
    !hoursForStore && locationId && getBranchHours(locationId);
  };

  useEffect(() => {
    handleGetBranchHours();
  }, []);

  useEffect(() => {
    if (locationId && !hoursForStore && isLocationTypeBranch) {
      handleGetBranchHours();
    }
  }, [locationId, hoursForStore, getBranchHours]);

  const handleDateChange = (date) => onDateChange(date, dateName);
  const handleTimeChange = (timeVal) => timeVal && onTimeChange(timeVal, timeName);

  /**
   * This function generates a list of time slots between currentDate and endDate,
   * incrementing by timeInterval (in minutes).
   */
  const getHours = (currentDate, parsedEndDate, timeInterval) => {
    const hoursList = [];
    const currentTime = currentDate.clone();

    while (currentTime.isBefore(parsedEndDate)) {
      hoursList.push(currentTime.clone());
      currentTime.add(timeInterval, 'minutes');
    }

    return hoursList;
  };

  /**
   * This function processes the hoursList (generated from getHours) and
   * categorizes the time slots based on availability (OPEN, AFTER HOURS, UNAVAILABLE, or CLOSED).
   * @param {*} hoursList - The list of time slots (generated from getHours).
   * @param {*} eligibilityData - Location availability details (standard hours, after-hours, etc.).
   * @param {*} timeType - Determines whether the function is handling pickup or return times.
   * @param {*} locationTimezoneData - The timezone of the location.
   * @param {*} isSameDayBPH - Checks if the return time is before the pickup time (for same-day returns).
   * @param {*} afterHoursProps - UI content to be displayed for after-hours Tooltip
   * @param {*} optionsGroups - Stores categorized time options (open, closed, after-hours).
   * @param {*} selectsStart - true if the user is selecting a pickup time, false if selecting a return time.
   */
  const generateTimeOptions = (
    hoursList,
    eligibilityData,
    timeType,
    locationTimezoneData,
    isSameDayBPH,
    afterHoursProps,
    optionsGroups,
    selectsStartData
  ) => {
    const getLastOptionsGroup = () => optionsGroups[optionsGroups.length - 1];

    hoursList.forEach((timeData) => {
      const availabilityStatus = isLocationAvailableByTime(eligibilityData, timeData, timeType, locationTimezoneData);
      const isUnavailable = availabilityStatus === DATE.AVAILABILITY_STATUS.UNAVAILABLE;
      const isAfterHours = availabilityStatus === DATE.AVAILABILITY_STATUS.AFTER;
      const isOpen = availabilityStatus === DATE.AVAILABILITY_STATUS.OPEN;

      if (isOpen) {
        getLastOptionsGroup().options.push({
          label: timeData.format('LT'),
          value: timeData.format(GMI_TIME_FORMAT),
          disabled: isSameDayBPH(timeData) || isUnavailable,
        });
      } else if (isAfterHours) {
        const headerLabel = i18n(
          selectsStartData ? 'bw_timepicker_after_hours_pickup_separator' : 'bw_timepicker_after_hours_return_separator'
        );
        const extraClass = 'time-picker__item--after-hours';

        if (!getLastOptionsGroup().name || getLastOptionsGroup().name !== headerLabel) {
          optionsGroups.push({ name: headerLabel, extraClass, tooltip: afterHoursProps, options: [] });
        }

        getLastOptionsGroup().options.push({
          label: timeData.format('LT'),
          value: timeData.format(GMI_TIME_FORMAT),
          disabled: isSameDayBPH(timeData) || isUnavailable,
          extraClass,
        });
      } else {
        const headerLabel = i18n('bw_time_selector_closed_hours_label');
        const extraClass = 'time-picker__item--closed-hours';

        if (!getLastOptionsGroup().name || getLastOptionsGroup().name !== headerLabel) {
          optionsGroups.push({ name: headerLabel, extraClass, options: [] });
        }

        getLastOptionsGroup().options.push({
          label: timeData.format('LT'),
          value: timeData.format(GMI_TIME_FORMAT),
          disabled: true,
          extraClass,
        });
      }
    });
  };

  /**
   * Returns JSX for the tooltip shown when a user clicks on "After Hours" tooltip icon.
   */
  const getAfterHoursProps = (selectsStartData) =>
    selectsStartData ? (
      <div>
        <h3 className='after-hours__title after-hours__title-pick-up-icon'>
          {i18n('bw_timepicker_after_hours_pickup_tooltip_title')}
        </h3>
        <p>{i18n('picking_up_vehicle_easy_alamo')}</p> <p>{i18n('after_hours_additional_steps_confirmation_email')}</p>
      </div>
    ) : (
      <div>
        <h3 className='after-hours__title after-hours__title-pick-up-icon'>
          {i18n('bw_timepicker_after_hours_return_tooltip_title')}
        </h3>
        <p>{i18n('returning_vehicle_location_closes_easy_alamo')}</p> <p>{i18n('tell_park_it_drop_keys_in_dropbox')}</p>
      </div>
    );

  /**
   * getTimeOptions function used to generate available time slots.
   * @param {*} date - The date for which the time slots are being generated.
   * @param {*} eligibilityData - Location eligibility data (open hours, closed hours, after-hours).
   * @returns
   */
  const getTimeOptions = (date, eligibilityData) => {
    const currentTime = getDateTimeObjFromTs(date || [], '').startOf('day');
    const endOfDay = getDateTimeObjFromTs(date || [], '').endOf('day');
    const isSameDay = !!endDate && endDate === startDate;
    const timeType = selectsStart ? 'PICKUP' : 'RETURN';

    const optionsGroups = [{ options: [] }];

    const afterHoursProps = getAfterHoursProps(selectsStart);

    const hoursList = getHours(currentTime, endOfDay, 30);

    // Processes and categorizes the available time slots.
    generateTimeOptions(
      hoursList,
      eligibilityData,
      timeType,
      locationTimezone,
      (currentTimeHour) => isSameDayBeforePickupHours(isSameDay, pickupTime, currentTimeHour),
      afterHoursProps,
      optionsGroups,
      selectsStart
    );

    return optionsGroups;
  };

  const isStartDateClosedOnDayDatePicker = (day, eligibilityData) => {
    if (!eligibilityData) return false;
    const dayKey = getDateTimeObjFromTs(day).format('YYYY-MM-DD');
    // Checking if PickUpDate is Closed? from 'AFTER' hours because pickup is possible 24/7
    // This will check is PickUpDate Closed? from 'STANDARD' hours If 'AFTER' is unavailable
    return !eligibilityData[dayKey]?.AFTER
      ? eligibilityData[dayKey]?.STANDARD?.closed
      : eligibilityData[dayKey]?.AFTER?.closed;
  };

  const isReturnDateClosedOnDayDatePicker = (day, eligibilityData) => {
    if (!eligibilityData) return false;
    const dayKey = getDateTimeObjFromTs(day).format('YYYY-MM-DD');
    // Checking if ReturnDate is Closed? from 'DROP' hours because return is possible 24/7
    // This will check is ReturnDate Closed? from 'STANDARD' hours If 'DROP' is unavailable
    return !eligibilityData[dayKey]?.DROP
      ? eligibilityData[dayKey]?.STANDARD?.closed
      : eligibilityData[dayKey]?.DROP?.closed;
  };

  const getSuggestedTimeOptionIndex = (optionsGroups) =>
    optionsGroups
      .map((item) => item.options)
      .flat()
      .findIndex((item) => item.label === SUGGESTED_TIME);

  const furthestDate = (latestDate && new Date(latestDate)) || maxDate;
  const start = startDate ? getDateTimeObjFromTs(startDate, 'YYYY-MM-DD').toDate() : null;
  const end = endDate ? getDateTimeObjFromTs(endDate, 'YYYY-MM-DD').toDate() : null;
  let selectedDay = null;
  const isPickUpDate = dateName === 'pickupDate';
  const timeOptions = isObjectEmpty(hoursForStore) ? eligibility : hoursForStore;
  const currentDate = isPickUpDate ? start : end;
  let startDateClosed = isStartDateClosedOnDayDatePicker(start, timeOptions);
  let returnDateClosed = isReturnDateClosedOnDayDatePicker(end, timeOptions);
  if (!isPickUpDate) {
    //  if we are on the return date branch selection,
    //  we want to show the pick up date regardless if it is closed or not
    startDateClosed = false;
  }

  if (one_way_rental && isPickUpDate) {
    // same as above but showing return date
    returnDateClosed = false;
  }

  if (selectsStart && !startDateClosed) {
    selectedDay = start;
  } else if (!selectsStart && !returnDateClosed) {
    selectedDay = end;
  }
  let optionsGroups = getTimeOptions(currentDate, timeOptions);
  //  if the start or end is closed we want to pass in an empty object
  //  otherwise times will all be closed when they are selecting a new date in
  //  unavailableLocationModal
  if (isPickUpDate && startDateClosed) {
    optionsGroups = getTimeOptions(currentDate, {});
  } else if (!isPickUpDate && returnDateClosed) {
    optionsGroups = getTimeOptions(currentDate, {});
  }
  const placesFromSuggestion = 6; // 6 places before 12pm should be 9am
  const suggestedOptionIndex = Math.max(getSuggestedTimeOptionIndex(optionsGroups) - placesFromSuggestion, 0); // suggested index should be 6 places before 12pm or 0

  const monthsShown = monthsShownDatePicker || isSixColumnGrid ? 1 : 2; // show only 1 month for 6 column BW, or if default value is provided use `monthsShownDatePicker`
  const rightAlignDatepicker = isSixColumnGrid && !isPickUpDate;

  return (
    <div className='fieldset fieldset-group fieldset--date-time'>
      <DatePicker
        name={dateName}
        startDate={startDateClosed ? null : start}
        endDate={returnDateClosed ? null : end}
        onChange={handleDateChange}
        selected={selectedDay}
        minDate={minDate}
        maxDate={furthestDate}
        helpText={helpText}
        accessibilityPrefixText={accessibilityPrefixText}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        id={dateId}
        label={dateLabel}
        guided={guided}
        eligibility={timeOptions}
        showErrorsOnLoad={showErrorsOnLoad}
        monthsShown={monthsShown}
        rightAlignDatepicker={rightAlignDatepicker}
        asterisk
        timeAndDatePickerFullHeight={timeAndDatePickerFullHeight}
        isUnavailableLocationContent={isUnavailableLocationContent}
        contractRestrictions={contractRestrictions}
        isRental={Boolean(latestDate)}
      />

      <ResponsiveListbox
        id={timeName}
        name={timeName}
        onChange={handleTimeChange}
        className='time-picker'
        label={i18n('common_time')}
        fullLabel={timeLabel}
        mobileLabel={i18n('bw_departure_time_prompt_mobile')}
        fill
        optionsGroups={optionsGroups}
        guided={guided}
        lastUpdated={lastUpdated}
        suggestedOptionIndex={suggestedOptionIndex}
        suggestedOption={SUGGESTED_TIME}
        showErrorsOnLoad={showErrorsOnLoad}
        asterisk
        useMobileVersionForTablet
        time={time}
        timeAndDatePickerFullHeight={timeAndDatePickerFullHeight}
      />
    </div>
  );
};

DateTimeFieldset.propTypes = {
  dateId: PropTypes.string.isRequired,
  dateLabel: PropTypes.string.isRequired,
  dateName: PropTypes.string.isRequired,
  endDate: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  selectsStart: PropTypes.bool,
  startDate: PropTypes.string,
  timeId: PropTypes.string.isRequired,
  timeLabel: PropTypes.string.isRequired,
  timeName: PropTypes.string.isRequired,
  eligibility: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  dateError: PropTypes.bool,
  timeError: PropTypes.bool,
  hoursForStore: PropTypes.any,
  isSixColumnGrid: PropTypes.bool,
  monthsShownDatePicker: PropTypes.number,
  timeAndDatePickerFullHeight: PropTypes.bool,
  contractRestrictions: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  locationTimezone: PropTypes.string,
  latestDate: PropTypes.string,
  isUnavailableLocationContent: PropTypes.bool,
};

export default DateTimeFieldset;
